import { Button } from '@design-system/components/button/button';
import { ExternalLink } from '@design-system/components/link/externalLink';
import { SideModal } from '@design-system/components/modal/sideModal';
import { Typography } from '@design-system/components/typography/typography';
import { sprinkles } from '@design-system/sprinkles.css';
import { clsx } from 'clsx';
import { Divider } from '@mui/material';
import { Alert } from '@design-system/components/alert/alert';
import { SubscriptionBundlesVM } from '../../viewModel/subscriptionBundles.viewModel';
import { SubscriptionFreeTrial } from './SubscriptionFreeTrial';
import { SubscriptionPriceSetup } from './SubscriptionPriceSetup';
import { SubscriptionDiscounts } from './SubscriptionDiscounts';
import { LandingPageInfo } from './LandingPageInfo';

const exclusivePriceHelpLink =
  'https://help.playbookapp.io/en/articles/9698798-setting-up-your-exclusive-subscription-formerly-called-pay-per-creator-ppc';

export const SubscriptionPriceModal = ({
  isOpen,
  close,
  cta,
  control,
  freeTrial,
  error,
  discounts,
  transformPrice,
  landingPage,
  publishWarning,
}: {
  isOpen: boolean;
  close: () => void;
} & Pick<
  SubscriptionBundlesVM['priceEditModal'],
  | 'control'
  | 'freeTrial'
  | 'error'
  | 'discounts'
  | 'transformPrice'
  | 'landingPage'
  | 'cta'
  | 'publishWarning'
>) => {
  return (
    <SideModal
      isOpen={isOpen}
      onClose={close}
      noSpacing
      testId="subscriptionPriceModal"
    >
      <div className="flex flex-col gap-6 px-12 py-6 w-full">
        <div className="flex justify-between">
          <Typography variant="title">Edit</Typography>
          <div className="flex gap-4 items-center">
            {cta.map(({ label, onClick, submitting, disabled, color }) => (
              <Button
                key={label}
                color={color || 'neutral'}
                onClick={onClick}
                loading={submitting}
                disabled={disabled}
              >
                {label}
              </Button>
            ))}
          </div>
        </div>
        {publishWarning && (
          <Alert
            variant="warning"
            details="Please save your changes before publishing offer."
          />
        )}
        <div className="flex flex-col gap-6 w-full overflow-y-auto">
          {error && (
            <Alert variant="error" details={error} title="Missing price data" />
          )}
          <div
            className={clsx(
              'flex flex-col md:flex-row justify-between w-full',
              sprinkles({ bg: 'W-100' }),
            )}
          >
            <SubscriptionPriceSetup
              control={control}
              freeTrial={freeTrial}
              transformPrice={transformPrice}
            />
            <div
              className={clsx(
                'w-[300px] h-[200px] flex flex-col gap-2 p-4 rounded-[10px]',
                sprinkles({
                  bg: 'N-10',
                }),
              )}
            >
              <Typography variant="body" size="medium">
                How to price a subscription?
              </Typography>
              <Typography variant="body" size="medium" fontWeight="normal">
                Pricing is specific to you and the amount of effort you'll be
                putting into your offering. We've compiled our best practices
                and great ways to think about this in our Pricing guide.
              </Typography>
              <ExternalLink
                label="Read more"
                href={exclusivePriceHelpLink}
                textVariant="body"
                textSize="medium"
                textColor="P-80"
              />
            </div>
          </div>
          <Divider />
          <SubscriptionFreeTrial control={control} freeTrial={freeTrial} />
          <Divider />
          <SubscriptionDiscounts discounts={discounts} />
          <Divider />
          <LandingPageInfo landingPage={landingPage} />
        </div>
      </div>
    </SideModal>
  );
};
